import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import api from '../../services/api'

export default function AuditNew() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<any[]>([])
  const [sites, setSites] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [form, setForm] = useState({
    template_id: '',
    audit_type: 'whs',
    site_id: '',
    auditor_id: '',
    scheduled_date: new Date().toISOString().slice(0, 10),
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      api.get('/audits/templates').catch(() => ({ data: { data: [] } })),
      api.get('/auth/sites').catch(() => ({ data: [] })),
      api.get('/users').catch(() => ({ data: [] })),
    ]).then(([t, s, u]) => {
      setTemplates(t.data.data || [])
      setSites(s.data || [])
      setUsers(u.data || [])
    })
  }, [])

  // Auto-set audit_type from template
  const handleTemplate = (id: string) => {
    const tmpl = templates.find(t => t.id === id)
    setForm(f => ({ ...f, template_id: id, audit_type: tmpl?.audit_type || f.audit_type }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.auditor_id || !form.scheduled_date) {
      setError('Auditor and scheduled date are required')
      return
    }
    setLoading(true)
    setError('')
    try {
      const { data } = await api.post('/audits', {
        ...form,
        template_id: form.template_id || undefined,
        site_id: form.site_id || undefined,
      })
      navigate(`/audits/${data.id}/conduct`)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create audit')
    }
    setLoading(false)
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>New Audit</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Schedule an audit and begin conducting it</p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        <div>
          <label>Template <span className="font-normal text-xs" style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
          <select value={form.template_id} onChange={e => handleTemplate(e.target.value)}>
            <option value="">— No template (blank audit) —</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {templates.length === 0 && (
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              No templates yet — <a href="/audits/templates/new" style={{ color: 'var(--accent)' }}>create one first</a> for structured audits
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label>Audit Type *</label>
            <select value={form.audit_type} onChange={e => setForm(f => ({ ...f, audit_type: e.target.value }))}>
              <option value="whs">WHS</option>
              <option value="environmental">Environmental</option>
              <option value="quality">Quality</option>
              <option value="contractor">Contractor</option>
              <option value="fire_safety">Fire Safety</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>
          <div>
            <label>Site</label>
            <select value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}>
              <option value="">— All sites —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label>Lead Auditor *</label>
            <select value={form.auditor_id} onChange={e => setForm(f => ({ ...f, auditor_id: e.target.value }))} required>
              <option value="">— Select auditor —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select>
          </div>
          <div>
            <label>Scheduled Date *</label>
            <input type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} required />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
          </div>
        )}

        <div className="flex gap-3">
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create & Conduct'}</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/audits')}>Cancel</button>
        </div>
      </form>
    </div>
  )
}
